import { useState, useEffect } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Shield, ShieldOff, X, Plus } from "lucide-react";

export function ListSelector({ api, selected = [], onChange }) {
  const [availableLists, setAvailableLists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/lists/for-select/all")
      .then(res => setAvailableLists(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api]);

  const addList = (listId) => {
    if (listId && !selected.includes(listId)) {
      onChange([...selected, listId]);
    }
  };

  const removeList = (listId) => {
    onChange(selected.filter(id => id !== listId));
  };

  const getListInfo = (id) => availableLists.find(l => l.id === id);
  const unselected = availableLists.filter(l => !selected.includes(l.id));

  return (
    <div className="space-y-3" data-testid="list-selector">
      {/* Selected lists */}
      {selected.length > 0 && (
        <div className="space-y-2">
          {selected.map(id => {
            const info = getListInfo(id);
            if (!info) return null;
            return (
              <div key={id} className="flex items-center justify-between p-2.5 border rounded-lg bg-muted/20">
                <div className="flex items-center gap-2">
                  {info.list_type === "allowed"
                    ? <Shield className="h-4 w-4 text-emerald-500" />
                    : <ShieldOff className="h-4 w-4 text-red-500" />
                  }
                  <span className="text-sm font-medium">{info.name}</span>
                  <Badge variant="outline" className="text-[10px]">{info.category}</Badge>
                  <Badge variant="outline" className={`text-[10px] ${info.list_type === "allowed" ? "text-emerald-600" : "text-red-600"}`}>
                    {info.list_type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{info.item_count} items</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeList(id)} className="h-6 w-6 p-0">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add list dropdown */}
      {unselected.length > 0 && (
        <div className="flex items-center gap-2">
          <Select onValueChange={addList}>
            <SelectTrigger className="flex-1" data-testid="add-filter-list">
              <SelectValue placeholder="Add a filter list..." />
            </SelectTrigger>
            <SelectContent>
              {unselected.map(l => (
                <SelectItem key={l.id} value={l.id}>
                  <span className="flex items-center gap-2">
                    {l.list_type === "allowed" ? <Shield className="h-3 w-3 text-emerald-500" /> : <ShieldOff className="h-3 w-3 text-red-500" />}
                    {l.name} — {l.category} ({l.item_count})
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {selected.length === 0 && unselected.length === 0 && !loading && (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No lists available. Create lists in the List Manager.
        </p>
      )}

      {selected.length === 0 && unselected.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Select lists to filter bid requests. Allowed lists = only matching requests pass. Blocked lists = matching requests are rejected.
        </p>
      )}
    </div>
  );
}
